import React, { useEffect, useState } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaMapMarkerAlt, FaPhone, FaClock, FaEye, FaPrint } from 'react-icons/fa';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';
import { fetchStoreBranches, clearError } from '../../../redux/Slices/AdminStoresSlice';
import PrintInvoicesModal from '../../../shared/components/PrintInvoicesModal';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const AdminStoreBranches = () => {
    const { setTitle } = useTitle();
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const { branches, branchesPagination, branchesLoading: loading, error } = useSelector((state) => state.adminStores);
    const [currentPage, setCurrentPage] = useState(1);
    const [printModal, setPrintModal] = useState({ isOpen: false, storeId: null });

    // دالة لتصحيح عرض رقم الهاتف
    const formatPhoneNumber = (phone) => {
        if (!phone) return 'غير محدد';
        
        // تحويل إلى نص للتأكد
        let phoneStr = String(phone).trim();
        
        // إزالة أي مسافات أو رموز غير مرغوب فيها
        phoneStr = phoneStr.replace(/\s+/g, '');
        
        // إذا كان الرقم يبدأ بـ + (مثل +201025376604) - نحوله إلى 201025376604+
        if (phoneStr.startsWith('+')) {
            return phoneStr.slice(1) + '+';
        }
        
        // إذا كان الرقم ينتهي بـ + (مثل 201025376604+) - نتركه كما هو
        if (phoneStr.endsWith('+')) {
            return phoneStr;
        }
        
        // إذا كان الرقم بدون + (مثل 201025376604) - نضيف + في النهاية
        return phoneStr + '+';
    };

    useEffect(() => {
        setTitle('فروع المتجر');
        dispatch(fetchStoreBranches({ storeId: id, page: currentPage }));
    }, [setTitle, id, currentPage, dispatch]);

    useEffect(() => {
        if (error) {
            showErrorToast(error);
            dispatch(clearError());
        }
    }, [error, dispatch]);

    const handleViewDetails = (branchId) => {
        navigate(`/admin-stores/${id}/branches/${branchId}`);
    };

    const getStatusBadge = (status) => {
        if (status === "1" || status === 1 || status === true) {
            return <span className="badge bg-success text-white">نشط</span>;
        }
        return <span className="badge bg-danger text-white">غير نشط</span>;
    };

    const renderPagination = () => {
        const totalPages = branchesPagination?.last_page || branchesPagination?.total_pages || 1;
        const pages = [];
        
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                    <button
                        className="page-link"
                        onClick={() => setCurrentPage(i)}>
                        {i}
                    </button>
                </li>
            );
        }

        return (
            <nav aria-label="Page navigation">
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}>
                            السابق
                        </button>
                    </li>
                    {pages}
                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}>
                            التالي
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn btn-outline-secondary d-flex align-items-center"
                            onClick={() => navigate('/admin-stores')}
                            style={{ borderRadius: '8px' }}>
                            <FaArrowLeft className="mx-2" />
                            العودة للمتاجر
                        </motion.button>
                    </div>
                </div>

                {/* Branches Table */}
                <div className="card shadow-lg border-0">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3 text-muted">جاري تحميل الفروع...</p>
                            </div>
                        ) : !Array.isArray(branches) || branches.length === 0 ? (
                            <div className="text-center py-5">
                                <FaMapMarkerAlt className="text-muted" style={{ fontSize: '3rem' }} />
                                <p className="mt-3 text-muted">لا توجد فروع لهذا المتجر</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>اسم الفرع</th>
                                                <th>رقم الهاتف</th>
                                                <th>البريد الإلكتروني</th>
                                                <th>حالة النشاط</th>
                                                <th>حالة الحظر</th>
                                                <th>تاريخ التسجيل</th>
                                                <th>الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {branches.map((branch, index) => (
                                                <tr key={branch.id}>
                                                    <td>{(currentPage - 1) * 10 + index + 1}</td>
                                                    <td>
                                                        <div className="fw-medium">{branch.market_name || branch.name || branch.branch_name || 'غير محدد'}</div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <FaPhone className="text-muted mx-2" />
                                                            {formatPhoneNumber(branch.phone)}
                                                        </div>
                                                    </td>
                                                    <td>{branch.email || 'غير محدد'}</td>
                                                    <td>
                                                        {getStatusBadge(branch.status || branch.is_active)}
                                                    </td>
                                                    <td>
                                                        {branch.ban === "1" || branch.ban === 1 ? (
                                                            <span className="badge bg-danger text-white">محظور</span>
                                                        ) : (
                                                            <span className="badge bg-success text-white">غير محظور</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {branch.created_at ? new Date(branch.created_at).toLocaleDateString('ar-EG') : 'غير محدد'}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <motion.button
                                                                whileHover={{ scale: 1.05 }}
                                                                whileTap={{ scale: 0.95 }}
                                                                className="btn btn-sm text-white"
                                                                onClick={() => handleViewDetails(branch.id)}
                                                                title="عرض التفاصيل"
                                                                style={{ backgroundColor: 'rgb(24, 65, 148)' }}>
                                                                <FaEye />
                                                            </motion.button>
                                                            <motion.button
                                                            whileHover={{ scale: 1.05 }}
                                                            whileTap={{ scale: 0.95 }}
                                                                className="btn btn-sm text-white"
                                                                onClick={() => setPrintModal({ isOpen: true, storeId: id })}
                                                                title="طباعة فواتير"
                                                                style={{ backgroundColor: '#10b981' }}>
                                                                <FaPrint style={{ fontSize: '1rem' }} />
                                                            </motion.button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {renderPagination()}
                            </>
                        )}
                    </div>
                </div>
            </div>

            <PrintInvoicesModal
                isOpen={printModal.isOpen}
                onClose={() => setPrintModal({ isOpen: false, storeId: null })}
                storeId={printModal.storeId}
                baseUrl={BASE_URL}
            />
        </div>
    );
};

export default AdminStoreBranches;
